import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
  Server,
  ShieldCheck,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

type Provider = 'resend' | 'postmark' | 'sendgrid' | 'custom';
type TestStatus = 'queued' | 'sent' | 'bounced' | null;

interface SmtpRow {
  user_id: string;
  provider: Provider;
  host: string;
  port: number;
  username: string;
  password: string;
  from_email: string;
  from_name: string;
  secure: boolean;
  last_test_status: TestStatus;
  last_test_at: string | null;
  last_test_error: string | null;
  last_test_message_id: string | null;
}

const PROVIDER_PRESETS: Record<
  Provider,
  { host: string; port: number; secure: boolean; username: string; helper: string; docsUrl: string; label: string }
> = {
  resend: {
    host: 'smtp.resend.com',
    port: 465,
    secure: true,
    username: 'resend',
    helper: 'Use your Resend API key as the password. The username is always "resend".',
    docsUrl: 'https://resend.com/docs/send-with-smtp',
    label: 'Resend',
  },
  postmark: {
    host: 'smtp.postmarkapp.com',
    port: 587,
    secure: false,
    username: '',
    helper: 'Username and password are both your Postmark Server API token.',
    docsUrl: 'https://postmarkapp.com/developer/user-guide/send-email-with-smtp',
    label: 'Postmark',
  },
  sendgrid: {
    host: 'smtp.sendgrid.net',
    port: 587,
    secure: false,
    username: 'apikey',
    helper: 'Username is the literal string "apikey". Password is your SendGrid API key.',
    docsUrl: 'https://docs.sendgrid.com/for-developers/sending-email/integrating-with-the-smtp-api',
    label: 'SendGrid',
  },
  custom: {
    host: '',
    port: 587,
    secure: false,
    username: '',
    helper: 'Enter your own SMTP host, port, username and password.',
    docsUrl: 'https://supabase.com/docs/guides/auth/auth-smtp',
    label: 'Custom SMTP',
  },
};

const SUPABASE_AUTH_URL_DOC = 'https://supabase.com/dashboard/project/_/auth/url-configuration';
const SUPABASE_AUTH_SMTP_DOC = 'https://supabase.com/dashboard/project/_/settings/auth';

const StatusPill: React.FC<{ status: TestStatus }> = ({ status }) => {
  if (!status) return null;
  const map: Record<NonNullable<TestStatus>, { label: string; cls: string; Icon: any }> = {
    queued: {
      label: 'Queued',
      cls: 'bg-amber-50 border-amber-200 text-amber-800',
      Icon: Loader2,
    },
    sent: {
      label: 'Sent',
      cls: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      Icon: CheckCircle2,
    },
    bounced: {
      label: 'Bounced / failed',
      cls: 'bg-rose-50 border-rose-200 text-rose-700',
      Icon: XCircle,
    },
  };
  const { label, cls, Icon } = map[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${cls}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'queued' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  );
};

const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-100 text-fuchsia-700 font-bold text-sm flex items-center justify-center">
      {n}
    </div>
    <div className="flex-1">
      <div className="font-semibold text-slate-900 text-sm">{title}</div>
      <div className="text-sm text-slate-600 mt-1 leading-relaxed">{children}</div>
    </div>
  </div>
);

const CopyField: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs font-mono text-slate-800 break-all">
        {value}
      </code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 rounded border border-slate-200 hover:bg-slate-50 text-xs text-slate-700"
      >
        <Copy className="w-3.5 h-3.5" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

const SmtpSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user, isOwner, isAdmin, loading, isAuthenticated } = useAuth();

  const [provider, setProvider] = useState<Provider>('resend');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(465);
  const [secure, setSecure] = useState(true);
  const [username, setUsername] = useState('resend');
  const [password, setPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('KinsCircle');

  const [loadingRow, setLoadingRow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err' | 'info'; msg: string } | null>(null);

  const [lastStatus, setLastStatus] = useState<TestStatus>(null);
  const [lastTestAt, setLastTestAt] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  // Pre-fill from auth
  useEffect(() => {
    if (user?.email && !fromEmail) setFromEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Load existing config
  useEffect(() => {
    if (!user) {
      setLoadingRow(false);
      return;
    }
    (async () => {
      setLoadingRow(true);
      const { data, error } = await supabase
        .from('smtp_config')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) {
        console.error('[SmtpSettings] load failed', error);
      }
      const row = data as SmtpRow | null;
      if (row) {
        setProvider((row.provider as Provider) || 'resend');
        setHost(row.host || '');
        setPort(row.port || 587);
        setSecure(!!row.secure);
        setUsername(row.username || '');
        setPassword(row.password || '');
        setFromEmail(row.from_email || user.email || '');
        setFromName(row.from_name || 'KinsCircle');
        setLastStatus((row.last_test_status as TestStatus) ?? null);
        setLastTestAt(row.last_test_at);
        setLastError(row.last_test_error);
      }
      setLoadingRow(false);
    })();
  }, [user]);

  const applyPreset = (p: Provider) => {
    setProvider(p);
    const preset = PROVIDER_PRESETS[p];
    // Only overwrite host/port/secure/username if blank or matching another preset's values.
    setHost(preset.host || host || '');
    setPort(preset.port);
    setSecure(preset.secure);
    if (preset.username) setUsername(preset.username);
    setBanner(null);
  };

  const validate = (): string | null => {
    if (!host.trim()) return 'SMTP host is required.';
    if (!port || port < 1) return 'A valid port is required (usually 465 or 587).';
    if (!username.trim()) return 'SMTP username is required.';
    if (!password.trim()) return 'SMTP password / API key is required.';
    if (!fromEmail.trim() || !fromEmail.includes('@')) return 'A valid From email is required.';
    return null;
  };

  const buildPayload = () => ({
    user_id: user!.id,
    provider,
    host: host.trim(),
    port: Number(port),
    username: username.trim(),
    password: password.trim(),
    from_email: fromEmail.trim(),
    from_name: fromName.trim() || 'KinsCircle',
    secure,
    updated_at: new Date().toISOString(),
  });

  const handleSave = async () => {
    if (!user) return;
    const err = validate();
    if (err) {
      setBanner({ kind: 'err', msg: err });
      return;
    }
    setSaving(true);
    setBanner(null);
    try {
      const { error } = await supabase
        .from('smtp_config')
        .upsert(buildPayload(), { onConflict: 'user_id' });
      if (error) {
        setBanner({ kind: 'err', msg: `Save failed: ${error.message}` });
      } else {
        setSavedAt(new Date().toISOString());
        setBanner({ kind: 'ok', msg: 'SMTP settings saved.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!user) return;
    // NOTE: The send-test-email edge function now uses the Resend HTTP API
    // (https://api.resend.com/emails) authenticated with the RESEND_API_KEY
    // server-side secret. The smtp_config row above is preserved for
    // historical/auth-SMTP wiring, but is NOT used to send the test email
    // anymore. The validate() check is intentionally skipped on the test
    // path so an operator can verify Resend works before filling out the
    // legacy SMTP form.
    setTesting(true);
    setBanner(null);
    setLastStatus('queued');
    setLastTestAt(new Date().toISOString());
    setLastError(null);

    console.log('[SmtpSettings] Sending test email via Resend HTTP API…', {
      endpoint: 'https://api.resend.com/emails',
      method: 'POST',
      auth: 'Bearer process.env.RESEND_API_KEY (server-side)',
      to: user.email,
    });

    try {
      const t0 = performance.now();
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: { to: user.email, source: 'smtp-settings-page' },
      });
      const ms = Math.round(performance.now() - t0);
      console.log('[SmtpSettings] send-test-email response:', { data, error, ms });

      if (error) {
        // Network / non-2xx from the edge function. Log clearly, do NOT throw.
        console.error('[SmtpSettings] Resend test failed (transport):', error);
        setLastStatus('bounced');
        setLastError(error.message);
        setBanner({
          kind: 'err',
          msg: `Resend test failed: ${error.message}. The app did not crash — check the browser console for full details.`,
        });
      } else if (data?.ok) {
        // Resend accepted the request. data.id is the Resend message id.
        const messageId = data?.id || data?.messageId || data?.resend?.id || null;
        const provider = data?.provider || 'resend';
        console.log('[SmtpSettings] Resend accepted message:', { messageId, provider });
        setLastStatus('sent');
        setLastTestAt(new Date().toISOString());
        setBanner({
          kind: 'ok',
          msg:
            `Test email sent to ${user.email} via Resend` +
            (messageId ? ` (id: ${messageId})` : '') +
            `. Check your inbox (and spam folder).`,
        });
      } else {
        // Edge function returned ok:false with a structured error message.
        // The function never crashes — it always returns JSON.
        const msg =
          data?.error ||
          data?.message ||
          'Resend rejected the request and returned no error message.';
        console.error('[SmtpSettings] Resend test rejected:', data);
        setLastStatus('bounced');
        setLastError(msg);
        setBanner({
          kind: 'err',
          msg: `Resend test failed: ${msg}`,
        });
      }
    } catch (e: any) {
      // Defensive — supabase.functions.invoke shouldn't throw, but if a
      // CORS/network error slips through we log + surface it without
      // taking down the page.
      console.error('[SmtpSettings] Unexpected error during Resend test:', e);
      setLastStatus('bounced');
      setLastError(e?.message || 'Unexpected error');
      setBanner({
        kind: 'err',
        msg: `Unexpected error: ${e?.message || 'unknown'}. The app did not crash — see console for stack trace.`,
      });
    } finally {
      setTesting(false);
    }
  };


  const presetHelper = useMemo(() => PROVIDER_PRESETS[provider].helper, [provider]);
  const presetDocs = useMemo(() => PROVIDER_PRESETS[provider].docsUrl, [provider]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-fuchsia-600" />
          <h1 className="mt-3 text-xl font-bold text-slate-900">Sign in to access email settings</h1>
          <p className="text-slate-600 mt-2">Only the family owner can configure SMTP delivery.</p>
          <Button className="mt-5" onClick={() => navigate('/')}>Back to home</Button>
        </div>
      </div>
    );
  }

  if (!isOwner && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-amber-500" />
          <h1 className="mt-3 text-xl font-bold text-slate-900">Owner-only settings</h1>
          <p className="text-slate-600 mt-2">
            SMTP configuration controls how all account emails (password reset, magic links,
            invitations) are sent. Only the family owner can change it.
          </p>
          <Button className="mt-5" onClick={() => navigate('/')}>Back to home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to KinsCircle
        </button>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Email delivery (SMTP)</h1>
            <p className="text-slate-600 mt-1">
              Configure custom SMTP so password reset and magic-link emails actually deliver in production.
            </p>
          </div>
        </div>
        {/* Dev-bypass banner: shown when there's no real Supabase session
            (the temporary auto-login-as-owner bypass in AuthContext is active). */}
        {!user && (
          <div className="mb-6 bg-sky-50 border border-sky-200 rounded-xl p-5 flex gap-3">
            <Info className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
            <div className="text-sm text-sky-900">
              <div className="font-semibold mb-1">Dev-bypass mode is active</div>
              You're viewing this page without a real signed-in session. You can fill out the form
              below and copy the values into the Supabase dashboard manually (steps 1–3 below).
              To use the <strong>Save settings</strong> and <strong>Send test email</strong> buttons,
              sign in with your owner account first — those operations require a real auth session
              for row-level security.
            </div>
          </div>
        )}
        {/* Active provider banner — confirms outbound email is now on Resend */}
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <div className="font-semibold mb-1">Outbound email: Resend HTTP API</div>
            All app-generated email (invitations, test sends, notifications) now goes through{' '}
            <code className="px-1 py-0.5 bg-emerald-100 rounded">https://api.resend.com/emails</code>{' '}
            using the server-side <code className="px-1 py-0.5 bg-emerald-100 rounded">RESEND_API_KEY</code> secret.
            SendGrid and SMTP credentials below are kept only for the optional Supabase-Auth password-reset / magic-link
            wiring in step 3 — they are <strong>not</strong> used by the <em>Send test email</em> button.
          </div>
        </div>

        {/* Why this matters */}
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold mb-1">Why custom SMTP is still useful for Supabase Auth</div>
            Supabase's built-in email sender is rate-limited to about <strong>3–4 emails per hour</strong> and is intended
            for development only. In production it silently drops messages, which is why password
            reset and magic-link emails appear "sent" but never arrive. Configuring custom SMTP here fixes that — but
            invitations and notifications already deliver through Resend regardless.
          </div>
        </div>


        {/* Provider picker */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-bold text-slate-900">1. Choose a provider</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(['resend', 'postmark', 'sendgrid', 'custom'] as Provider[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => applyPreset(p)}
                className={`px-3 py-3 rounded-lg border text-sm font-semibold transition ${
                  provider === p
                    ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700 ring-2 ring-fuchsia-200'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {PROVIDER_PRESETS[p].label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-3 leading-relaxed">
            {presetHelper}{' '}
            <a
              href={presetDocs}
              target="_blank"
              rel="noopener noreferrer"
              className="text-fuchsia-600 hover:underline inline-flex items-center gap-0.5"
            >
              Provider docs <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>

        {/* Credentials */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <h2 className="text-base font-bold text-slate-900 mb-4">2. SMTP credentials</h2>

          {loadingRow ? (
            <div className="py-8 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="from_email">From email</Label>
                <Input
                  id="from_email"
                  type="email"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder="noreply@kinscircleapp.com"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Must be a verified sender on your provider — otherwise emails will bounce.
                </p>
              </div>
              <div>
                <Label htmlFor="from_name">From name</Label>
                <Input
                  id="from_name"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="KinsCircle"
                />
              </div>
              <div>
                <Label htmlFor="host">SMTP host</Label>
                <Input id="host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.resend.com" />
              </div>
              <div>
                <Label htmlFor="port">Port</Label>
                <Input
                  id="port"
                  type="number"
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  placeholder="465"
                />
              </div>
              <div>
                <Label htmlFor="secure" className="flex items-center justify-between">
                  <span>SSL/TLS</span>
                  <input
                    id="secure"
                    type="checkbox"
                    checked={secure}
                    onChange={(e) => setSecure(e.target.checked)}
                    className="w-4 h-4 accent-fuchsia-600"
                  />
                </Label>
                <p className="text-[11px] text-slate-500 mt-1">
                  Enable for port 465. Port 587 typically uses STARTTLS (leave off).
                </p>
              </div>
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={provider === 'sendgrid' ? 'apikey' : 'username'}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="password">Password / API key</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="•••••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleSave}
              disabled={saving || loadingRow}
              variant="outline"
              className="border-slate-300"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save settings
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={testing || loadingRow}
              className="bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send test email
            </Button>
            <div className="flex-1" />
            <div className="flex items-center gap-2 self-center">
              <span className="text-xs text-slate-500">Last test:</span>
              <StatusPill status={lastStatus} />
            </div>
          </div>

          {savedAt && !banner && (
            <p className="text-[11px] text-emerald-700 mt-3">Saved at {new Date(savedAt).toLocaleString()}</p>
          )}

          {banner && (
            <div
              className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                banner.kind === 'ok'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : banner.kind === 'err'
                  ? 'bg-rose-50 border-rose-200 text-rose-800'
                  : 'bg-sky-50 border-sky-200 text-sky-800'
              }`}
            >
              {banner.msg}
            </div>
          )}

          {lastTestAt && (
            <div className="mt-3 text-xs text-slate-500">
              Last attempted: {new Date(lastTestAt).toLocaleString()}
              {lastError && (
                <div className="mt-1 text-rose-700 font-mono break-all">SMTP error: {lastError}</div>
              )}
            </div>
          )}
        </div>

        {/* Supabase wiring instructions */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-4 h-4 text-slate-500" />
            <h2 className="text-base font-bold text-slate-900">3. Wire it into Supabase Auth</h2>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            The test button above proves your credentials work. To make Supabase use them for the
            password-reset and magic-link emails it sends from <code>auth.resetPasswordForEmail</code> and{' '}
            <code>auth.signInWithOtp</code>, paste the same values into your Supabase project's Auth settings.
          </p>

          <div className="space-y-5">
            <Step n={1} title="Open Supabase → Project Settings → Auth → SMTP Settings">
              <a
                href={SUPABASE_AUTH_SMTP_DOC}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fuchsia-600 hover:underline inline-flex items-center gap-1"
              >
                Open Supabase Auth settings <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </Step>
            <Step n={2} title="Enable “Custom SMTP” and paste these values">
              <div className="mt-2 space-y-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Sender email</div>
                  <CopyField value={fromEmail || '(set From email above)'} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Sender name</div>
                  <CopyField value={fromName || 'KinsCircle'} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Host</div>
                    <CopyField value={host || '(set host above)'} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Port</div>
                    <CopyField value={String(port || '')} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Username</div>
                    <CopyField value={username || '(set username above)'} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Password</div>
                    <CopyField value={password ? '••••••••  (use the password you entered above)' : '(set password above)'} />
                  </div>
                </div>
              </div>
            </Step>
            <Step n={3} title="Allow-list redirect URLs">
              In <strong>Authentication → URL Configuration → Redirect URLs</strong> add:
              <div className="mt-2 space-y-2">
                <CopyField value="https://www.kinscircleapp.com" />
                <CopyField value="https://www.kinscircleapp.com/*" />
                <CopyField value="https://www.kinscircleapp.com/reset-password" />
              </div>
              <a
                href={SUPABASE_AUTH_URL_DOC}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-fuchsia-600 hover:underline"
              >
                Open URL configuration <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </Step>
            <Step n={4} title="Save in Supabase, then trigger a real password-reset from the login screen">
              The reset email should arrive within seconds via your provider. If it doesn't, re-run the
              test above — the error message will tell you exactly what failed (auth, DNS, sender not verified, etc.).
            </Step>
          </div>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Credentials are stored in your private <code>smtp_config</code> row, secured with row-level security so only
          you can read or modify them.
        </p>
      </div>
    </div>
  );
};

export default SmtpSettings;
